import { TargetStatus } from "@biggeo/bg-server-lib/studio";
import { ComputeStatus } from "@biggeo/bg-server-lib/studio";
import {
    Box,
    Button,
    CompanyAvatar,
    IconButton,
    LoadingBar,
    Menu,
    MenuItem,
    OverflowingTypography,
    Stack,
    Switch,
    Typography,
} from "@biggeo/bg-ui/lab";
import { DeleteOutline, MoreVertOutline } from "@biggeo/bg-ui/lab/icons";
import { IDataSampleCardContainer } from "../containers/DataSampleCardContainer";
import { getRandomColor } from "@biggeo/bg-utils";

interface IDataSampleCard extends IDataSampleCardContainer {
    isBuyNowDisabled?: boolean;
}

const DataSampleCard = ({
    sample,
    onBuyNowClick,
    onContactSalesClick,
    onSwitchClick,
    onDeleteClick,
    isBuyNowDisabled = false,
}: IDataSampleCard) => {
    const isLoading =
        sample.dataSourceConfiguration.status === ComputeStatus.loading;
    const isHot =
        sample.dataSourceConfiguration.status === ComputeStatus.hot &&
        sample.dataSourceConfiguration.targetState === TargetStatus.hot;

    return (
        <Stack>
            {isLoading && (
                <LoadingBar
                    color="primary"
                    sx={{
                        borderTopLeftRadius: (theme) => theme.radius.xs5,
                        borderTopRightRadius: (theme) => theme.radius.xs5,
                        height: 2,
                    }}
                />
            )}
            <Stack
                width="100%"
                height="100%"
                sx={{
                    border: 1,
                    borderColor: (theme) => theme.palette.stroke[100],
                    borderTopLeftRadius: (theme) => theme.radius.xs5,
                    borderTopRightRadius: (theme) => theme.radius.xs5,
                }}
            >
                <Box
                    sx={{
                        borderTopLeftRadius: "inherit",
                        borderTopRightRadius: "inherit",
                        height: 2,
                        width: "100%",
                        background: isLoading
                            ? undefined
                            : `linear-gradient(90deg, #EBEFFE 0%, ${sample.color ?? getRandomColor()} 100%)`,
                    }}
                />
                <Stack
                    width="100%"
                    gap={2}
                    sx={{
                        paddingX: isLoading ? 2 : undefined,
                        padding: isLoading ? undefined : 2,
                    }}
                >
                    <Stack
                        width="100%"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Stack
                            width="100%"
                            flexDirection="row"
                            gap={2}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    padding: 1,
                                    backgroundColor: (theme) =>
                                        theme.palette.surface.container,
                                    borderRadius: (theme) =>
                                        theme.radius.default,
                                }}
                            >
                                <CompanyAvatar
                                    size="sm"
                                    src={sample.image}
                                    square
                                />
                            </Box>
                            <Stack>
                                <OverflowingTypography
                                    variant="body3"
                                    fontWeight="semibold"
                                    sx={{
                                        maxWidth: (theme) => theme.spacing(41),
                                    }}
                                >
                                    {sample.dataSource.name}
                                </OverflowingTypography>
                                {sample.organization && (
                                    <OverflowingTypography
                                        variant="body4"
                                        textColor="disabled.onContainer"
                                    >
                                        {`By ${sample.organization}`}
                                    </OverflowingTypography>
                                )}
                            </Stack>
                        </Stack>
                        {onDeleteClick && (
                            <Menu
                                placement="bottom-end"
                                content={
                                    <MenuItem
                                        startNode={
                                            <DeleteOutline color="error" />
                                        }
                                        label="Delete Sample"
                                        color="error"
                                        density="dense"
                                        onClick={onDeleteClick}
                                    />
                                }
                            >
                                <IconButton variant="minimal">
                                    <MoreVertOutline />
                                </IconButton>
                            </Menu>
                        )}
                    </Stack>
                    <Stack
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Typography
                            variant="body3"
                            textColor="disabled.onContainer"
                        >
                            View Sample
                        </Typography>
                        <Switch
                            switched={sample.isSelected}
                            disabled={!isHot}
                            onSwitchChange={() => {
                                onSwitchClick({
                                    dataSourceId: sample.dataSource.id,
                                    isSelected: !sample.isSelected,
                                });
                            }}
                        />
                    </Stack>
                    <Stack flexDirection="row" gap={4}>
                        <Button
                            color="primary"
                            onClick={() => {
                                if (sample.fkMarketplaceDatasetId) {
                                    onBuyNowClick(
                                        sample.fkMarketplaceDatasetId
                                    );
                                }
                            }}
                            disabled={
                                isBuyNowDisabled ||
                                !sample.fkMarketplaceDatasetId
                            }
                        >
                            Buy Now
                        </Button>
                        <Button
                            variant="minimal"
                            onClick={() => {
                                if (sample.fkMarketplaceDatasetId) {
                                    onContactSalesClick(
                                        sample.fkMarketplaceDatasetId
                                    );
                                }
                            }}
                            disabled={!sample.fkMarketplaceDatasetId}
                        >
                            Contact Sales
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default DataSampleCard;
